import { ColumnDefinitionInputV1, ColumnTypeEnum } from '@/sdk/model/ColumnDefinitionInputV1';
import { ColumnDefinitionOutputV1 } from '@/sdk/model/ColumnDefinitionOutputV1';
import { TableDefinitionInputV1 } from '@/sdk/model/TableDefinitionInputV1';
import { TableDefinitionOutputV1 } from '@/sdk/model/TableDefinitionOutputV1';
import { RecomputeColumnTypeEnum } from '@/sdk/api/TableDefinitionsApi';
import {
  ColumnRuleFormulaCreatorInputV1,
  ColumnRuleInputV1,
  DatasourceOutputV1,
  GraphQLInputV1,
  GraphQLOutputV1,
  ItemSearchPreviewV1,
  sqDatasourcesApi,
  sqGraphQLApi,
  sqItemsApi,
  sqTableDefinitionsApi,
  TableComputeOutputV1,
} from '@/sdk';
import { addTableDefinition } from '@/workbook/workbook.actions';
import { addBackingTable, addColumnToFormulaAcrossTable, setMode } from '@/tableBuilder/tableBuilder.actions';
import {
  AgGridScalingColumnDefinition,
  BooleanTableCell,
  EditorType,
  FormulaCompileResult,
  ItemTableCell,
  NumericTableCell,
  ProcessedMaterializedTable,
  ProcessedTableCell,
  ScalingTableColumnDefinition,
  TableDefinitionActions,
  TextTableCell,
  TreeHierarchyNode,
} from '@/tableDefinitionEditor/tableDefinition.types';
import { runFormula as compileFormulaOrThrowError } from '@/formula/formula.utilities';
import { t } from 'i18next';
import {
  ColumnRule,
  ColumnRuleInput,
  ColumnTypeOptions,
  CombinedColumnRuleInputParameters,
} from '@/tableDefinitionEditor/columnRules/columnRule.constants';
import {
  columnRuleOutputToColumnRuleInput,
  getRuleTypeFromRuleInput,
} from '@/tableDefinitionEditor/columnRules/columnRule.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { setDisplayItemTableEditor, setDisplayTableDefinitionEditor, setView } from '@/worksheet/worksheet.actions';
import _ from 'lodash';
import {
  ADD_TO_TREE_PSEUDO_RULE,
  ColumnRulesWithMetaData,
  CREATE_TREE_PSEUDO_RULE,
  RULES_WITH_PERMISSIONS,
} from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.constants';
import { ColumnRuleWithMetadata } from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.types';
import { fullyArchiveDatasource } from '@/utilities/datasources.utilities';
import { sqWorkbenchStore } from '@/core/core.stores';
import { FormulaErrorInterface } from '@/formula/formula.types';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { Column, ColumnState, GridApi } from 'ag-grid-community';
import { setDatasources } from '@/search/search.actions';
import type { SelectedSearchItem } from '@/search/search.types';
import { resetItemFinder } from '@/itemFinder/itemFinder.actions';
import { ColumnDefinitionBulkEditInputV1 } from '@/sdk/model/models';
import { fetchMaterializedTable } from '@/tableDefinitionEditor/materializedTable.utilities';
import { MaterializedTablePropertyColumnInput } from '@/tableDefinitionEditor/materializedTable.types';
import {
  GARBAGE_OBJECT_CHAR,
  SCALING_TABLE_DEFINITION_CANCELLATION_GROUP,
} from '@/tableDefinitionEditor/tableDefinition.constants';
import {
  setAutoFocusTrendTab,
  setItemTableEditorVisible,
  setTableDefinitionEditorVisible,
} from '@/main/layout.actions';
import { WORKSHEET_VIEW } from '@/worksheet/worksheet.constants';
import { ITEM_TYPES } from '@/trendData/trendData.constants';
import { addTrendItem, setScalingColumnsUpdated } from '@/trendData/trend.actions';
import { TableDefinitionStore } from '@/tableDefinitionEditor/tableDefinition.store';
import { resetTableDefinition, setTableDefinition } from '@/tableDefinitionEditor/tableDefinition.actions';
import { resetTableEditorStore, setTableId } from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { NULL_PLACEHOLDER, TableBuilderMode } from '@/tableBuilder/tableBuilder.constants';
import { RuleControl } from '@/tableDefinitionEditor/hooks/useRuleState.hook';

export const getRuleTypeAndParameters = (
  columnRuleInput: ColumnRuleInput,
): {
  ruleType: ColumnRule;
  parameters: Partial<CombinedColumnRuleInputParameters>;
} => {
  const ruleType = getRuleTypeFromRuleInput(columnRuleInput);
  const parameters = columnRuleInput[ruleType]!;
  return { ruleType, parameters };
};

export const getColumnTypeFromText = (text: string): ColumnTypeEnum | undefined => {
  const columnType = ColumnTypeOptions.find((option) => t(option.label) === text);
  return columnType?.value;
};

export const getColumnRuleWithMetaDataFromLabel = (label: string): ColumnRuleWithMetadata | undefined =>
  ColumnRulesWithMetaData.find((rule) => rule.label === label);

export const getColumnRuleWithMetaDataFromRule = (rule: string): ColumnRuleWithMetadata => {
  // In the case of Constant rules, the rule comes back like "stringConstant" whereas the ruleWithLabel is just
  // "constant", causing the exception to be thrown. Instead, we match on whether the rule contains "constant" as a
  // substring
  const columnRule = ColumnRulesWithMetaData.find(
    (ruleWithLabel) =>
      ruleWithLabel.rule === rule ||
      (ruleWithLabel.rule === 'constant' && rule.toLowerCase().includes(ruleWithLabel.rule.toLowerCase())),
  );
  if (!columnRule) {
    throw new Error(`Column rule ${rule} not found`);
  }
  return columnRule;
};

export const buildRowDataForAgGrid = (
  agGridColumnDefinitions: AgGridScalingColumnDefinition[],
  materializedTable: ProcessedMaterializedTable | undefined,
) => {
  if (!materializedTable) {
    return null;
  }

  const materializedTableHeaders = materializedTable.headers;
  const rows = materializedTable.rows;
  return rows.map((row) => {
    const rowData: Record<string, any> = {};
    materializedTableHeaders.forEach((header: any, index: number) => {
      const agColumn = agGridColumnDefinitions.find((columnDef) => columnDef.headerName === header.name);
      const field = agColumn?.field;
      if (field) {
        rowData[field] = row[index];
      }
    });
    return rowData;
  });
};

export const tableDefinitionOutputToTableDefinitionInput = (
  tableDefinition: TableDefinitionOutputV1,
  scopedTo?: string,
): TableDefinitionInputV1 => {
  return {
    name: tableDefinition.name,
    description: tableDefinition.description,
    dataId: tableDefinition.dataId,
    datasourceClass: tableDefinition.datasourceClass,
    datasourceId: tableDefinition.datasourceId,
    scopedTo: tableDefinition.scopedTo,
    subscriptionId: tableDefinition.subscription?.id,
    batchAction: tableDefinition.batchAction,
    columnDefinitions: tableDefinition.columnDefinitions.map((columnDef) =>
      columnDefinitionOutputToColumnDefinitionInput(columnDef, tableDefinition.columnDefinitions),
    ),
    tableType: tableDefinition.tableType,
  };
};

export const columnDefinitionOutputToColumnDefinitionInput = (
  columnDefinition: ColumnDefinitionOutputV1,
  otherColumns: ColumnDefinitionOutputV1[],
): ColumnDefinitionBulkEditInputV1 => {
  return {
    columnId: columnDefinition.id,
    columnType: columnDefinition.columnType,
    columnUom: columnDefinition.columnUom,
    columnName: columnDefinition.columnName,
    columnRules: columnDefinition.rules
      .filter((rule) => !rule.rule.includes(SeeqNames.MaterializedTables.Rules.Overrides.Override))
      .map((rule) => columnRuleOutputToColumnRuleInput(rule, otherColumns)),
    isIndexed: columnDefinition.isIndexed,
    isHidden: columnDefinition.isHidden,
    sortAscending: columnDefinition.sortAscending,
    sortIndex: columnDefinition.sortIndex,
    isGenerated: columnDefinition.isGenerated,
    description: columnDefinition.description,
    displayProperty: columnDefinition.displayProperty,
  };
};

export const removeColumnDefinition = async (
  tableDefinitionId: string,
  columnDefinitionId: string,
  forceDelete = false,
) => {
  const { data: tableDefinitionOutput } = await sqTableDefinitionsApi.deleteColumnFromTableDefinition({
    id: tableDefinitionId,
    columnId: columnDefinitionId,
    forceDelete,
  });

  return tableDefinitionOutput;
};

export const removeColumnOverrides = async (tableDefinitionId: string, columnDefinitionId: string) => {
  const { data: tableDefinitionOutput } = await sqTableDefinitionsApi.deleteOverridesFromColumn({
    id: tableDefinitionId,
    columnId: columnDefinitionId,
  });
  return tableDefinitionOutput;
};

export const addOrUpdateColumnDefinition = async (
  tableDefinitionId: string,
  columnDefinition: ColumnDefinitionBulkEditInputV1,
  columnDefinitionId?: string,
  skipRuleCompute = false,
): Promise<TableComputeOutputV1 | undefined> => {
  columnDefinition = {
    ...columnDefinition,
    columnId: columnDefinitionId,
  };
  const addOrUpdatePromise = sqTableDefinitionsApi.modifyColumnsInTableDefinition(
    { columnDefinitions: [columnDefinition] },
    {
      id: tableDefinitionId,
      recomputeColumnType: skipRuleCompute
        ? RecomputeColumnTypeEnum.NONE
        : RecomputeColumnTypeEnum.PROVIDEDWITHDEPENDENTS,
      includeDetailedResults: true,
    },
    { cancellationGroup: tableDefinitionId },
  );

  try {
    const { data: tableComputeOutput } = await addOrUpdatePromise;

    if (columnDefinition.columnRules && columnDefinition.columnRules.some((rule) => rule.treePathCreator)) {
      setDatasources('main', sqWorkbenchStore.stateParams.workbookId, true);
    }
    return tableComputeOutput;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

/**
 * When updating, this function will update properties of the table definition except for columns
 */
export const createOrUpdateTableDefinition = async (
  tableDefinition: TableDefinitionInputV1,
  tableDefinitionId?: string,
): Promise<TableDefinitionOutputV1> => {
  const { data: tableDefinitionOutput } = await (tableDefinitionId
    ? sqTableDefinitionsApi.updateTableDefinition(tableDefinition, { id: tableDefinitionId })
    : sqTableDefinitionsApi.createTableDefinition(tableDefinition));
  addTableDefinition(tableDefinitionOutput);

  return tableDefinitionOutput;
};

export const updateTableDefinitionName = async ({
  tableDefinitionId,
  name,
}: {
  tableDefinitionId: string;
  name: string;
}) => await sqItemsApi.setProperty({ value: name }, { id: tableDefinitionId, propertyName: SeeqNames.Properties.Name });

export const deleteTableRows = async (tableDefinitionId: string, rowIds: object[]) => {
  const REMOVE_MATERIALIZED_TABLE_ROWS =
    'mutation DeleteRows($tableId: String!, $rowIds: [RowIdInput!]!) {' +
    ' deleteRows(tableId: $tableId, rowIds: $rowIds) }';
  const inputObject: GraphQLInputV1 = {
    query: REMOVE_MATERIALIZED_TABLE_ROWS,
    variables: {
      tableId: tableDefinitionId,
      rowIds,
    },
  };
  try {
    const response = await sqGraphQLApi.graphql(inputObject);
    if (response.data.errors) {
      errorToast({ httpResponseOrError: response });
    }
    return response.data;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
    return {};
  }
};

export const getMaterializedTable = async (
  tableDefinitionId: string,
  columnsToInclude?: string[],
  propertiesToInclude?: MaterializedTablePropertyColumnInput[],
  cancellationGroup?: string,
  limit?: number,
  cursorKey?: string,
): Promise<GraphQLOutputV1> => {
  const response = await fetchMaterializedTable({
    tableDefinitionId,
    columnsToInclude,
    propertiesToInclude,
    includeOverrides: true,
    cancellationGroup,
    limit,
    cursorKey,
  });
  if (response.errors) {
    errorToast({ httpResponseOrError: response });
  }
  return response;
};

/**
 * Validates a formula for an instance of a formulaCreatorRule. In order to compile the formula, this function will
 * find the first viable item in each column that the formula references and use those as parameters. If no viable
 * items are found, the function will return an error. If viable parameters are found but there is a compilation
 * error, the compilation error will be returned.
 */
export const validateFormulaForCreator = async (
  formulaRule: ColumnRuleFormulaCreatorInputV1,
  materializedTable?: ProcessedMaterializedTable,
  columnDefinitions?: ColumnDefinitionOutputV1[],
): Promise<FormulaCompileResult> => {
  if (!materializedTable || !columnDefinitions || columnDefinitions.length === 0) {
    return {
      success: false,
      errors: [{ message: 'No items in the table to run the formula on', column: -1, line: -1 }],
    };
  }
  const errors: FormulaErrorInterface[] = [];
  const materializedTableHeaders = materializedTable.headers;
  const parameters = formulaRule.parameters || [];
  const columnIndexes = formulaRule.columnIndexes || [];

  const parametersForCompile = parameters
    .map((parameter, index) => {
      const indexOfColumnInTableDefinition = columnIndexes[index] - 1;
      const columnName = columnDefinitions[indexOfColumnInTableDefinition].columnName;
      const indexOfColumnInMaterializedTable = materializedTableHeaders.findIndex(
        (header) => header.name === columnName,
      );
      const rowWithValidItem = materializedTable.rows.find((row) =>
        isItemTableCell(row[indexOfColumnInMaterializedTable]),
      );
      const idOfFirstValidItemInGivenColumn = rowWithValidItem
        ? (rowWithValidItem[indexOfColumnInMaterializedTable] as ItemTableCell).uuid
        : undefined;

      if (idOfFirstValidItemInGivenColumn === undefined && formulaRule.formula.includes(parameter)) {
        errors.push({
          message: `No valid items in column ${columnName} which is referred to by parameter: ${parameter}`,
          column: -1,
          line: -1,
        });
      }
      return `${parameter}=${idOfFirstValidItemInGivenColumn}`;
    })
    .filter((param) => param.includes('undefined') === false);

  if (errors.length > 0) {
    return { success: false, errors };
  }

  let formulaResult = formulaRule.formula;
  formulaRule.variableParameters?.forEach((parameter, index) => {
    const variableParams = formulaRule.variableParameterStrings
      ?.at(index)
      ?.split(',')
      ?.map((param) => param.trim())
      .map((param) => param.replace('$', ''))
      .filter((param) => parametersForCompile.some((paramForCompile) => paramForCompile.includes(`${param}=`)))
      .map((param) => `$${param}`);
    formulaResult = `$${parameter}=parameters(${variableParams?.join(',')})\n${formulaResult}`;
  });
  try {
    await compileFormulaOrThrowError(formulaResult, parametersForCompile);
    return { success: true, errors: [] };
  } catch (e: any) {
    return { success: false, errors: e };
  }
};

export async function getExistingTableDefinition(
  tableDefinitionId: string,
  setTableDefinition: (tableDefinitionOutput: TableDefinitionOutputV1) => void,
) {
  try {
    const { data: tableDefinitionData } = await sqTableDefinitionsApi.getTableDefinition({ id: tableDefinitionId });
    setTableDefinition(tableDefinitionData);
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
}

export async function editTableDefinition(tableDefinition: SelectedSearchItem, editorType: EditorType) {
  resetItemFinder();
  resetTableDefinition(editorType);
  resetTableEditorStore(editorType);
  try {
    const { data: tableDefinitionData } = await sqTableDefinitionsApi.getTableDefinition(
      { id: tableDefinition.id },
      {
        cancellationGroup: SCALING_TABLE_DEFINITION_CANCELLATION_GROUP,
      },
    );
    setTableId(editorType, tableDefinitionData.id);
    setTableDefinition(editorType, tableDefinitionData);
    if (editorType === 'SCALING') {
      setDisplayTableDefinitionEditor(true);
    } else if (editorType === 'ITEM') {
      setDisplayItemTableEditor(true);
    }
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
}

export const isItemTableCell = (cell: any): cell is ItemTableCell => {
  return cell && cell.uuid && typeof cell.uuid === 'string';
};

export const isNumericTableCell = (cell: any): cell is NumericTableCell => {
  return cell && typeof cell.value === 'number';
};

export const isTextTableCell = (cell: any): cell is TextTableCell => {
  return cell && typeof cell.value === 'string';
};

export const isBooleanTableCell = (cell: any): cell is BooleanTableCell => {
  return cell && typeof cell.value === 'boolean';
};

/**
 * Retrieves the data source for a table definition.
 */
export const getTableDefinitionDataSource = async (
  tableDefinitionId: string,
): Promise<DatasourceOutputV1 | undefined> => {
  const {
    data: { datasources },
  } = await sqDatasourcesApi.getDatasources({
    datasourceId: tableDefinitionId.toLocaleLowerCase(),
  });

  return datasources?.at(0);
};

/**
 * Deletes datasource associated with a table definition which
 * will delete all created items associated with the table definition.
 */
export async function deleteTableItems(tableDefinitionId: string): Promise<void> {
  const tableDefinitionDatasource = await getTableDefinitionDataSource(tableDefinitionId);
  if (tableDefinitionDatasource) {
    await fullyArchiveDatasource(tableDefinitionDatasource.id);
  }
}

export const getColumnsContainingRulesWithPermissions = (
  columns: ColumnDefinitionOutputV1[],
): ColumnDefinitionOutputV1[] =>
  columns.filter((column) => column.rules.some((rule) => RULES_WITH_PERMISSIONS.includes(rule.rule)));

export const updateColumnDefinitions = async (
  columnsToUpdate: ColumnDefinitionBulkEditInputV1[],
  tableDefinitionId: string,
  skipRuleCompute = false,
  includeDetailedResults = true,
): Promise<TableComputeOutputV1 | undefined> => {
  if (_.size(columnsToUpdate) <= 0) {
    return undefined;
  }
  try {
    const { data: tableComputeOutput } = await sqTableDefinitionsApi.modifyColumnsInTableDefinition(
      { columnDefinitions: columnsToUpdate },
      {
        id: tableDefinitionId,
        recomputeColumnType: skipRuleCompute
          ? RecomputeColumnTypeEnum.NONE
          : RecomputeColumnTypeEnum.PROVIDEDWITHDEPENDENTS,
        includeDetailedResults,
      },
      { cancellationGroup: tableDefinitionId },
    );

    return tableComputeOutput;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

const updateColumnRuleScope = (columnRules: ColumnRuleInputV1[], scopedTo?: string): ColumnRuleInputV1[] => {
  return columnRules.map((columnRule) => {
    const ruleType = Object.keys(columnRule)[0] as keyof ColumnRuleInputV1;
    if (RULES_WITH_PERMISSIONS.includes(ruleType)) {
      return {
        [ruleType]: {
          ...columnRule[ruleType],
          scopedTo,
        },
      };
    }
    return columnRule;
  });
};

/**
 * Updates the columns containing rules with permissions with the new
 * access settings
 */
export const updateColumnsContainingRulesWithScope = async (
  tableDefinition: TableDefinitionOutputV1,
  workbookId?: string,
): Promise<TableDefinitionOutputV1 | undefined> => {
  const columnsToUpdate = getColumnsContainingRulesWithPermissions(tableDefinition.columnDefinitions).map((column) =>
    columnDefinitionOutputToColumnDefinitionInput(column, tableDefinition.columnDefinitions),
  );
  const columnsToUpdateWithUpdatedRules = columnsToUpdate.map((column) => ({
    ...column,
    columnRules: updateColumnRuleScope(column.columnRules, tableDefinition.scopedTo),
  }));

  if (columnsToUpdateWithUpdatedRules.some((column) => column.columnRules.some((rule) => rule.treePathCreator))) {
    await setDatasources('main', workbookId, true);
  }

  try {
    const columnUpdate = await updateColumnDefinitions(
      columnsToUpdateWithUpdatedRules,
      tableDefinition.id,
      false,
      true,
    );

    return _.isEmpty(columnsToUpdateWithUpdatedRules) ? tableDefinition : columnUpdate?.tableDefinition;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

/**
 * Updates the visibility of columns in a table based on user actions.
 *
 * @param tableDefinitionId
 * @param existingColumnsAsOutputs
 * @param existingColumnsAsInputs
 * @param agColumns - The AgGrid columns pulled from the api at time of execution
 * @param setTableDefinition - set the table definition in the store
 * @returns Promise<void>
 */
export const onColumnHideTriggeredByUser = async (
  tableDefinitionId: string,
  existingColumnsAsOutputs: ScalingTableColumnDefinition[],
  existingColumnsAsInputs: ColumnDefinitionInputV1[],
  agColumns: Column[],
  setTableDefinition: (tableDefinitionOutput: TableDefinitionOutputV1) => void,
) => {
  const columnIdToColumnInputMap: Record<string, ColumnDefinitionInputV1> = existingColumnsAsOutputs.reduce(
    (acc, column, index) => {
      acc[column.id] = existingColumnsAsInputs[index];
      return acc;
    },
    {} as Record<string, ColumnDefinitionInputV1>,
  );

  const tableColumnDefinitionsWithUpdatedVisibility: ColumnDefinitionBulkEditInputV1[] = (
    agColumns
      .map((agColumn) => {
        const columnId = agColumn.getColDef().field;
        const columnInput = columnId !== undefined ? columnIdToColumnInputMap[columnId] : undefined;
        const visibilityChanged = columnInput && !columnInput?.isHidden !== agColumn.isVisible();
        return visibilityChanged
          ? {
              isVisibleNow: agColumn.isVisible(),
              columnInput,
              columnId,
            }
          : undefined;
      })
      .filter((agColumn) => !!agColumn) as {
      isVisibleNow: boolean;
      columnInput: ColumnDefinitionInputV1;
      columnId: string;
    }[]
  ).map((agColumn) => {
    const columnInput = agColumn.columnInput;
    return {
      ...columnInput,
      isHidden: !agColumn.isVisibleNow,
      columnId: agColumn.columnId,
    } as ColumnDefinitionBulkEditInputV1;
  });

  const updatedTableDefinition = await updateColumnDefinitions(
    tableColumnDefinitionsWithUpdatedVisibility,
    tableDefinitionId,
    true,
    false,
  );
  if (updatedTableDefinition) {
    setTableDefinition(updatedTableDefinition.tableDefinition);
  }
};

/**
 * Updates the column definitions based on the user's sorting changes in the table.
 *
 * @param tableDefinitionId
 * @param existingColumnsAsOutputs
 * @param existingColumnsAsInputs
 * @param agColumns The AgGrid columns pulled from the api at time of execution
 * @param tableDefinitionActions
 */
export const onSortChangedByUser = async (
  tableDefinitionId: string,
  existingColumnsAsOutputs: ScalingTableColumnDefinition[],
  existingColumnsAsInputs: ColumnDefinitionInputV1[],
  agColumns: Column[],
  tableDefinitionActions: TableDefinitionActions,
) => {
  const columnIdToColumnInputMap: Record<string, ColumnDefinitionInputV1> = existingColumnsAsOutputs.reduce(
    (acc, column, index) => {
      acc[column.id] = existingColumnsAsInputs[index];
      return acc;
    },
    {} as Record<string, ColumnDefinitionInputV1>,
  );
  const tableDefColumnsWithUpdatedSort = agColumns
    .map((agColumn) => {
      const columnId = agColumn.getColDef().field;
      const columnInput = columnId !== undefined ? columnIdToColumnInputMap[columnId] : undefined;
      const existingSortIndex = columnInput?.sortIndex;
      const existingPotentialSort = columnInput?.sortAscending ? 'asc' : 'desc';
      const existingSort = existingSortIndex !== undefined ? existingPotentialSort : undefined;
      const agSort = agColumn.getSort();
      const agSortIndex = agColumn.getSortIndex() ?? undefined;
      if (existingSort !== agSort || existingSortIndex !== agSortIndex) {
        return {
          ...columnInput,
          sortAscending: agSort === 'asc',
          sortIndex: agSortIndex,
          columnId,
        } as ColumnDefinitionBulkEditInputV1;
      }
      return undefined;
    })
    .filter((column) => !!column) as ColumnDefinitionBulkEditInputV1[];
  const updatedTableDefinition = await updateColumnDefinitions(tableDefColumnsWithUpdatedSort, tableDefinitionId, true);
  if (updatedTableDefinition) {
    tableDefinitionActions.setTableDefinition(updatedTableDefinition.tableDefinition);
  }
};

export const onColumnMovedByUser = async (
  tableDefinitionId: string,
  columns: ScalingTableColumnDefinition[],
  agGridColumns: Column[],
  affectedColumnId: string,
  toIndex: number,
  tableDefinitionActions: TableDefinitionActions,
) => {
  const columnsIdsNotInGrid = columns
    .filter((column) => !agGridColumns.find((agGridColumn) => agGridColumn.getColDef().field === column.id))
    .map((column) => column.id);

  const columnsIdsInGridInNewOrder = agGridColumns
    .map((agColumn) => agColumn.getColDef().field)
    .filter((columnId) => columnId !== affectedColumnId)
    .filter((columnId): columnId is string => columnId !== undefined);

  columnsIdsInGridInNewOrder.splice(toIndex, 0, affectedColumnId);
  const newOrder = columnsIdsNotInGrid.concat(columnsIdsInGridInNewOrder);
  const { data: updatedTableDefinition } = await sqTableDefinitionsApi.reorderColumnsInTableDefinition(
    { columnDefinitionIds: newOrder },
    { id: tableDefinitionId },
  );
  tableDefinitionActions.setTableDefinition(updatedTableDefinition);
};

export const getColumnInputBasedOnColumnName = (
  tableDefinition: TableDefinitionInputV1,
  name?: string,
): ColumnDefinitionInputV1 | undefined => {
  return tableDefinition.columnDefinitions.find((column) => column.columnName === name);
};

export const updateSortsInTheGrid = (tableDefinition: TableDefinitionOutputV1, agGridApi: GridApi) => {
  const columnStateForUpdatedSorts: ColumnState[] = tableDefinition.columnDefinitions
    .map((columnDefinition) => {
      const agColumn = agGridApi.getColumn(columnDefinition.id);
      const sortIndexFromAgColumn = agColumn?.getSortIndex();
      const sortDirection = agColumn?.getSort();
      const sortDirectionIsAscending = sortDirection === 'asc';

      const sortAscendingIsEqual = sortDirectionIsAscending === columnDefinition.sortAscending;
      const sortIndexIsEqual = sortIndexFromAgColumn === columnDefinition.sortIndex;
      if (!sortIndexIsEqual || !sortAscendingIsEqual) {
        const sortingIsOff = columnDefinition.sortIndex === undefined;
        const potentialSortDirection = columnDefinition.sortAscending ? 'asc' : 'desc';
        const sort = sortingIsOff ? null : potentialSortDirection;
        return {
          colId: columnDefinition.id,
          sort,
          sortIndex: columnDefinition.sortIndex ?? null,
        };
      } else {
        return undefined;
      }
    })
    .filter((col) => !!col) as ColumnState[];
  if (columnStateForUpdatedSorts.length > 0) {
    agGridApi.applyColumnState({ state: columnStateForUpdatedSorts });
  }
};

export const updateVisibilityInTheGrid = (tableDefinition: TableDefinitionOutputV1, agGridApi: GridApi) => {
  const columnStateForVisibilityChanged: ColumnState[] = tableDefinition.columnDefinitions
    .map((columnDefinition) => {
      const agColumn = agGridApi.getColumn(columnDefinition.id);
      const isHidden = !agColumn?.isVisible();
      if (columnDefinition.isHidden !== isHidden) {
        return {
          colId: columnDefinition.id,
          hide: columnDefinition.isHidden,
        };
      } else {
        return undefined;
      }
    })
    .filter((col) => !!col) as ColumnState[];
  if (columnStateForVisibilityChanged.length > 0) {
    agGridApi.applyColumnState({ state: columnStateForVisibilityChanged });
  }
};

export const generateTableName = (
  currentId: string,
  tablesInLocal: ItemSearchPreviewV1[],
  defaultName: string,
  currentName?: string,
): string => {
  if (!currentId && !currentName) {
    return `${t(defaultName)} ${tablesInLocal.length + 1}`;
  }
  return currentName || `${t(defaultName)} ${tablesInLocal.length + 1}`;
};

export const closeTableEditor = (editor: EditorType) => {
  if (editor === 'SCALING') {
    setDisplayTableDefinitionEditor(false);
    setTableDefinitionEditorVisible(false);
  } else {
    setDisplayItemTableEditor(false);
    setItemTableEditorVisible(false);
  }
  resetTableDefinition(editor);
  resetItemFinder();
  resetTableEditorStore(editor);
};

/**
 * Uses {@link RuleControl} to construct the final rule state and outputs the rule being edited if the save is
 * successful, otherwise undefined. Updates ruleControl to facilitate changes in the rule UI throughout the
 * component stack.
 * The returned {@link ColumnRuleInputV1} needs to be
 * saved to local state or otherwise consumed, as state will be cleared at the end of a successful operation.
 */
export const createOrUpdateColumnRule = async (
  ruleControl: RuleControl,
  materializedTable: ProcessedMaterializedTable | undefined,
  allColumnDefinitions: ScalingTableColumnDefinition[],
  skipFormulaValidation: boolean,
  savePseudoRuleRef: React.MutableRefObject<() => Promise<void>>,
): Promise<ColumnRuleInputV1 | undefined> => {
  const {
    canSave,
    columnRuleWithMetadata,
    ruleParametersState,
    setShowError,
    setColumnRuleWithMetadata,
    ruleParametersDispatch,
    setFormulaCompileResults,
  } = ruleControl;
  if (!canSave) {
    setShowError(true);
    return undefined;
  }
  await savePseudoRuleRef.current?.();
  let columnRule: ColumnRuleInputV1 = {
    [columnRuleWithMetadata!.rule]: ruleParametersState,
  };

  if (
    columnRuleWithMetadata!.rule === CREATE_TREE_PSEUDO_RULE ||
    columnRuleWithMetadata!.rule === ADD_TO_TREE_PSEUDO_RULE
  ) {
    columnRule = {
      treePathCreator: ruleParametersState,
    } as ColumnRuleInputV1;
  }

  const formulaCompileResults = await (columnRuleWithMetadata!.rule ===
    SeeqNames.MaterializedTables.Rules.FormulaCreator && !skipFormulaValidation
    ? validateFormulaForCreator(
        ruleParametersState as ColumnRuleFormulaCreatorInputV1,
        materializedTable,
        allColumnDefinitions,
      )
    : Promise.resolve({ success: true, errors: [] }));
  setFormulaCompileResults(formulaCompileResults);
  if (!formulaCompileResults.success) {
    return undefined;
  }

  ruleParametersDispatch('reset');
  setShowError(false);
  setColumnRuleWithMetadata(undefined);
  return columnRule;
};

/**
 * Converts the selected columns on the scaling table to a simple table, creating a backing table if not
 * present, and adding the columns to the trend.  Also focuses the trend tab and set the table worksheet view. Sets
 * indicators for isFormulaAcrossTableProcessing and isFormulaAcrossTable to true.
 */
export const toSimpleTable = async (tableDefinitionStore: TableDefinitionStore) => {
  const selectedColumns = tableDefinitionStore.tableDefinitionOutput.columnDefinitions.filter((col) =>
    tableDefinitionStore.selectedColumns.includes(col.id),
  );

  if (selectedColumns.some((col) => col.columnType !== ColumnTypeEnum.UUID)) {
    errorToast({ messageKey: 'SCALING.SIMPLE_TABLE_COLUMN_TYPE_ERROR' });
    return;
  }

  addColumnToFormulaAcrossTable(true, true, 'asset');
  setView(WORKSHEET_VIEW.TABLE);
  setMode(TableBuilderMode.Simple);
  setAutoFocusTrendTab(true);

  await addBackingTable(
    selectedColumns.map((col) => col.id),
    tableDefinitionStore.id,
  );

  setScalingColumnsUpdated(false);
  selectedColumns.forEach((col) => {
    const columnData = tableDefinitionStore.getColumnFromProcessedMaterializedTable(col.columnName);
    const firstItem = columnData.find((item) => !!item.uuid);
    const props = {
      sampleItemId: firstItem?.uuid,
      sampleItemName: firstItem?.fetchedProperties?.[SeeqNames.Properties.Name],
      tableId: tableDefinitionStore.id,
      tableName: tableDefinitionStore.name,
    };
    const scalingColumn: any = {
      id: col.id,
      name: tableDefinitionStore.getColumnDisplayName(col.columnName),
      itemType: ITEM_TYPES.SCALING_COLUMN,
    };
    addTrendItem(scalingColumn, props);
  });
  setScalingColumnsUpdated(true);
};

/**
 * Returns the icon for the given types (type + uom) for AG grid column menu display. If all types are the same, it
 * returns the need ag grid icon element for that type.
 * @param types
 */
export const getIconForTypes = (types: { type: string | undefined; uom: any }[]): string => {
  if (types.length > 0 && types.every((type) => type.type === types[0].type && type.uom === types[0].uom)) {
    return getIconForType(types[0].type, types[0].uom);
  }
  return '';
};

/**
 * Returns the icon classes for the given type and uom.
 * @param type
 * @param uom
 */
export const getIconForType = (type: string | undefined, uom: any): string => {
  switch (type) {
    case SeeqNames.Types.CalculatedSignal:
    case SeeqNames.Types.StoredSignal:
      return uom !== 'string' ? 'fa-sharp fa-regular fa-fw fc fc-series' : 'fa-sharp fa-regular fa-fw fa fa-font';
    case SeeqNames.Types.CalculatedCondition:
    case SeeqNames.Types.StoredCondition:
      return 'fa-sharp fa-regular fa-fw fc fc-capsule-set';
    case SeeqNames.Types.LiteralScalar:
    case SeeqNames.Types.CalculatedScalar:
      return 'fa-sharp fa-regular fa-fw fc fc-scalar';
    case SeeqNames.Types.ThresholdMetric:
      return 'fa-sharp fa-regular fa-fw fc fc-metric';
    default:
      return '';
  }
};

/**
 * Extracts the parent level from an event property rule string. The string should contain at most one instance of
 * parent[<parentLevel>].
 * @param string
 */
export const extractParentLevelFromString = (string: string) => {
  const parentRegex = new RegExp('parent\\[(\\d+)]');
  const matches: string[] | null = string.match(parentRegex);
  if (matches && matches.length === 2 && !isNaN(Number(matches[1]))) {
    return Number(matches[1]);
  } else {
    return undefined;
  }
};

/**
 * Extracts the child key from an event property rule string. The string should contain at most one instance of
 * children[<childKey>].
 * @param string
 */
export const extractChildKeyFromString = (string: string) => {
  const childrenRegex = new RegExp('children\\[(.+?)]');
  const matches: string[] | null = string.match(childrenRegex);
  if (matches && matches.length === 2) {
    return matches[1];
  } else {
    return undefined;
  }
};

/**
 * Extracts the property name from an event property rule string. If no parent[<level>] or children[<childKey>] elements
 * are found, the string is returned indicating a property on the search items source level
 * @param string
 */
export const extractPropertyNameFromString = (string: string) => {
  const propertyNameRegex = new RegExp(
    '(?:parent\\[\\d+]|children\\[.+?])(?:\\.(?:parent\\[\\d+]|children\\[.+?]))*\\.(.+)',
  );
  const matches: string[] | null = string.match(propertyNameRegex);
  if (matches && matches.length === 2) {
    return matches[1];
  } else {
    return string;
  }
};

/**
 * Checks if the ancestor column is an item column. This is used to determine if asset icon should display and if
 * the property ID should be used for the event property rule.
 * @param eventNodes
 * @param level
 * @param tableDefinitionStore
 */
export const isAncestorColumnAnItem = (
  eventNodes: TreeHierarchyNode[],
  level: number,
  tableDefinitionStore: TableDefinitionStore,
) => {
  return !eventNodes
    .map((node) => {
      const ancestorLevel = (extractParentLevelFromString(node.eventAccessor) ?? 0) + level;
      const childKey = extractChildKeyFromString(node.eventAccessor);

      if (childKey) {
        return tableDefinitionStore.columnOptions?.hierarchy?.get(ancestorLevel - 1)?.type !== undefined;
      } else {
        return tableDefinitionStore.columnOptions?.hierarchy?.get(ancestorLevel)?.type !== undefined;
      }
    })
    .some((hasItemType) => hasItemType === false);
};

/**
 * Returns the ancestor rules for the given event nodes in the source column. The ancestor rules are calculated into
 * event property rules based on the event node's event accessor and converting to the appropriate level for the rules.
 * @param eventNodes
 * @param level
 * @param isItemColumn
 */
export const getAncestorRules = (eventNodes: TreeHierarchyNode[], level: number, isItemColumn: boolean) => {
  return eventNodes.map((node) => {
    const ancestorLevel = (extractParentLevelFromString(node.eventAccessor) ?? 0) + level;
    const childKey = extractChildKeyFromString(node.eventAccessor);

    const propertyName = isItemColumn ? SeeqNames.Properties.Id : SeeqNames.Properties.Name;

    if (childKey && ancestorLevel === 1) {
      return {
        eventProperty: {
          propertyName,
        },
      };
    }

    return {
      eventProperty: {
        propertyName: childKey
          ? `parent[${ancestorLevel - 1}].${propertyName}`
          : `parent[${ancestorLevel}].${propertyName}`,
      },
    };
  });
};

/**
 * Returns the property rules for the given event nodes in the source column.
 * @param eventNodes
 * @param name
 */
export const getPropertyRules = (eventNodes: TreeHierarchyNode[], name: string) => {
  if (eventNodes === undefined || eventNodes.length === 0) {
    return [
      {
        eventProperty: {
          propertyName: `${name}`,
        },
      },
    ];
  }
  return eventNodes.map((node) => {
    const levelAccessor = node.eventAccessor === '' ? '' : `${node.eventAccessor}.`;
    return {
      eventProperty: {
        propertyName: `${levelAccessor}${name}`,
      },
    };
  });
};

/**
 * Returns the descendant rules for the given event nodes in the source column.
 * @param eventNodes
 * @param child
 */
export const getDescendantRules = (eventNodes: TreeHierarchyNode[], child: string) => {
  return eventNodes.map((node) => {
    const levelAccessor = node.eventAccessor === '' ? '' : `${node.eventAccessor}.`;
    return {
      eventProperty: {
        propertyName: `${levelAccessor}children[${child}].${SeeqNames.Properties.Id}`,
      },
    };
  });
};

/**
 * Returns the event nodes for the given materialized table column definition. The event nodes used to determine the
 * rules generated from the column shortcuts.
 * @param mtColumnDefinition
 * @param tableDefinitionStore
 */
export const findEventNodes = (
  mtColumnDefinition: ScalingTableColumnDefinition,
  tableDefinitionStore: TableDefinitionStore,
) => {
  return mtColumnDefinition.rules
    .filter((rule) => rule.rule === SeeqNames.MaterializedTables.Rules.EventProperty)
    .map((rule) => rule.arguments[SeeqNames.MaterializedTables.Parameters.PropertyName])
    .map((property) => {
      const parentLevel = extractParentLevelFromString(property);
      const childKey = extractChildKeyFromString(property);
      let eventNode = tableDefinitionStore.columnOptions?.hierarchy?.get(parentLevel ?? 0);
      if (childKey) {
        eventNode = eventNode?.children?.get(childKey);
      }
      return eventNode;
    })
    .filter((node) => node !== undefined) as TreeHierarchyNode[];
};

export const getColumnAncestorLevels = (
  mtColumnDefinition: ScalingTableColumnDefinition,
  tableDefinitionStore: TableDefinitionStore,
) => {
  return new Set(
    mtColumnDefinition.rules
      .filter((rule) => rule.rule === SeeqNames.MaterializedTables.Rules.EventProperty)
      .map((rule) => rule.arguments[SeeqNames.MaterializedTables.Parameters.PropertyName])
      .flatMap((property) => {
        const parentLevel = extractParentLevelFromString(property) ?? 0;
        const childKey = extractChildKeyFromString(property);

        return Array.from(tableDefinitionStore.columnOptions?.hierarchy?.keys() ?? [])
          .filter((key) => (childKey ? key >= parentLevel : key > parentLevel))
          .map((key) => key - parentLevel + (childKey ? 1 : 0));
      }),
  );
};

export const hasDescendantColumnOptions = (
  mtColumnDefinition: ScalingTableColumnDefinition,
  tableDefinitionStore: TableDefinitionStore,
): boolean => {
  if (!mtColumnDefinition) {
    return false;
  }
  const eventNodes = findEventNodes(mtColumnDefinition, tableDefinitionStore);
  const childrenOptions: Set<string> = new Set(eventNodes.flatMap((node) => Array.from(node.children?.keys() ?? [])));
  return childrenOptions.size > 0;
};

export const hasAncestorColumnOptions = (
  mtColumnDefinition: ScalingTableColumnDefinition,
  tableDefinitionStore: TableDefinitionStore,
): boolean => {
  if (!mtColumnDefinition) {
    return false;
  }
  const ancestorLevelOptions: Set<number> = getColumnAncestorLevels(mtColumnDefinition, tableDefinitionStore);
  return Array.from(ancestorLevelOptions).length > 0;
};

export const consolidatedValueFormatter = (params: { value: ProcessedTableCell; colDef?: any }): string => {
  const { value, colDef } = params;
  if (isItemTableCell(value)) {
    const displayProperty = colDef?.displayProperty;
    const fetchedProperties = value.fetchedProperties;
    const propertyValue = fetchedProperties?.[displayProperty];
    if (displayProperty && value.fetchedProperties) {
      return _.isNil(propertyValue) || propertyValue === GARBAGE_OBJECT_CHAR ? NULL_PLACEHOLDER : propertyValue;
    }

    return value?.uuid ?? NULL_PLACEHOLDER;
  }

  if (isBooleanTableCell(value)) {
    return value.value ? 'true' : 'false';
  }

  if (isNumericTableCell(value)) {
    return _.isNil(value?.value) ? NULL_PLACEHOLDER : `${value?.value}${value?.uom ?? ''}`;
  }

  if (isTextTableCell(value)) {
    return value?.value !== undefined && value.value !== GARBAGE_OBJECT_CHAR ? value.value : NULL_PLACEHOLDER;
  }

  return NULL_PLACEHOLDER;
};
